import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sparkle, ChartLineUp, ChatCircleDots, Star } from "@phosphor-icons/react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "demo@guestivate.com", password: "Guestivate123", property_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 grid-noise relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center font-heading font-bold text-xl">G</div>
            <span className="font-heading font-semibold text-lg tracking-tight">Guestivate</span>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold text-primary mb-5">
            <Sparkle size={14} weight="fill" /> AI-native CDP
          </div>
          <h2 className="font-heading text-4xl font-semibold tracking-tight leading-[1.1]">
            One guest profile.<br />Every channel, unified.
          </h2>
          <p className="text-white/60 mt-5 leading-relaxed">
            The customer data platform for independent hotels — powering email, chat, reputation and voice with a single, grounded source of truth.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-9">
            {[
              { icon: ChartLineUp, t: "Guest 360", d: "Unified timelines" },
              { icon: Star, t: "Reputation", d: "AI sentiment tagging" },
              { icon: ChatCircleDots, t: "AI Concierge", d: "Grounded answers" },
              { icon: Sparkle, t: "Email AI", d: "Drafts in seconds" },
            ].map((f) => (
              <div key={f.t} className="rounded-lg border border-white/10 bg-white/5 p-4">
                <f.icon size={20} weight="fill" className="text-primary" />
                <div className="font-medium text-sm mt-2.5">{f.t}</div>
                <div className="text-white/50 text-xs mt-0.5">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-white/40 text-xs">© 2026 Guestivate · Built for independent hotels</div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold text-xl">G</div>
            <span className="font-heading font-semibold text-lg">Guestivate</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your workspace"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1.5">
            {mode === "login" ? "Sign in to your property dashboard." : "Set up your hotel in under a minute."}
          </p>

          <form onSubmit={submit} className="mt-7 space-y-3.5">
            {mode === "register" && (
              <>
                <Field label="Your name">
                  <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="gv-input" placeholder="Alex Morgan" />
                </Field>
                <Field label="Hotel name">
                  <input data-testid="register-property" required value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} className="gv-input" placeholder="The Wren House" />
                </Field>
              </>
            )}
            <Field label="Email">
              <input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="gv-input" placeholder="you@hotel.com" />
            </Field>
            <Field label="Password">
              <input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="gv-input" placeholder="••••••••" />
            </Field>

            {error && <div data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">{error}</div>}

            <button data-testid="auth-submit" disabled={busy} className="w-full h-11 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-[0.99]">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create workspace"}
            </button>
          </form>

          {mode === "login" && (
            <div className="mt-4 text-xs text-muted-foreground bg-secondary rounded-md px-3 py-2.5 border border-border">
              <span className="font-medium text-foreground">Demo login</span> — prefilled above. Just hit Sign in.
            </div>
          )}

          <div className="mt-6 text-sm text-center text-muted-foreground">
            {mode === "login" ? "New to Guestivate? " : "Already have an account? "}
            <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-primary font-medium hover:underline">
              {mode === "login" ? "Create a workspace" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
      <style>{`.gv-input{width:100%;height:2.75rem;border:1px solid hsl(var(--border));border-radius:0.5rem;padding:0 0.85rem;font-size:14px;background:hsl(var(--background));outline:none;transition:border-color .15s,box-shadow .15s}.gv-input:focus{border-color:hsl(var(--primary));box-shadow:0 0 0 3px hsl(var(--primary)/0.12)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
